import { ChangeEvent, useRef, useState } from "react";
import Option from "@JohnLewisPartnership/jl-design-system/dist/components/option/Option";
import { IconButton } from "@JohnLewisPartnership/jl-design-system/dist/elements/button/IconButton";
import { InfoOutlined } from "@JohnLewisPartnership/jl-design-system/dist/components/icons/InfoOutlined";
import { CloseOutlined } from "@JohnLewisPartnership/jl-design-system/dist/components/icons/CloseOutlined";
import styles from "./ExtendedWarranties.module.scss";
import { Services } from "@/types/serviceTypes.ts";
import { SharedServicesActions } from "@/utils/services/useSharedServices.ts";

type Props = {
  /**
   * Indicates if a product variant is available to order.
   */
  availableToOrder: boolean;
  /**
   * Analytics Handler for extended warranties
   */
  extendedWarrantiesAnalyticsHandler: (warrantyTitle: string) => void;
  /** Available services — passed down from the container. */
  availableServices: Services;
  /** Store mutation actions — passed down from the container. */
  actions: SharedServicesActions;
};

export const ExtendedWarrantiesUI = ({
  availableToOrder,
  extendedWarrantiesAnalyticsHandler,
  availableServices,
  actions,
}: Props) => {
  const infoButtonRef = useRef<HTMLButtonElement>(null);
  const [showInfo, setShowInfo] = useState(false);

  const handleTooltipCollapseClick = () => setShowInfo(false);
  const handleTooltipExpandClick = () => {
    setShowInfo(!showInfo);
    infoButtonRef.current?.focus();
  };
  const handleToggleSelectedServiceClick =
    (warrantyId: string, warrantyTitle: string) =>
    (e: ChangeEvent<HTMLFormElement>) => {
      const isChecked = e.target?.checked;
      if (isChecked) {
        actions.selectService(warrantyId);
        extendedWarrantiesAnalyticsHandler(warrantyTitle);
      } else {
        actions.removeService(warrantyId);
      }
    };

  const warranties = availableServices.filter(
    (service) => service.serviceType === "ExtendedWarranty",
  );

  if (!warranties.length) return null;

  return (
    <div
      data-testid="product:services-and-warranties"
      data-component="ExtendedWarranties"
    >
      <div
        className={styles.extendedWarrantiesContainer}
        data-testid="product:extended-warranties"
      >
        {warranties.map((warranty, index) => {
          return (
            <section key={`warranty:${index}`}>
              <h2
                data-testid="product:extended-warranties:title"
                className={styles.title}
              >
                {warranty.title}
              </h2>
              <div className={styles.warrantyItem}>
                <Option
                  type="checkbox"
                  isControlled={true}
                  className={styles.warrantyInfo}
                  id={warranty.title}
                  ariaLabel={warranty.purchaseDescription}
                  label={warranty.purchaseDescription}
                  checked={warranty.selected}
                  onClick={handleToggleSelectedServiceClick(
                    warranty.id,
                    warranty.title,
                  )}
                  name={warranty.title}
                  value={""}
                  additionalInfo={warranty.display}
                  disabled={!availableToOrder}
                />
                <IconButton
                  IconComponent={InfoOutlined}
                  ariaLabel={`${warranty.title} information`}
                  data-testid="product:extended-warranty:tooltip:button:expand"
                  className={styles.warrantyToolTip}
                  aria-expanded={showInfo}
                  aria-controls={`extended-warranty-tooltip-${index}`}
                  ref={infoButtonRef}
                  onClick={handleTooltipExpandClick}
                  variant="clear-light"
                />
              </div>

              <div
                id={`extended-warranty-tooltip-${index}`}
                data-testid="product:extended-warranty:tooltip"
                className={styles.infoBlock}
                hidden={!showInfo}
              >
                <IconButton
                  IconComponent={CloseOutlined}
                  ariaLabel={"Collapse tooltip"}
                  data-testid="product:extended-warranty:tooltip:button:collapse"
                  className={styles.closeButton}
                  onClick={handleTooltipCollapseClick}
                  variant="clear-light"
                />
                <div aria-live="polite">
                  <div
                    dangerouslySetInnerHTML={{
                      __html: `${warranty.tooltip}`,
                    }}
                  />
                </div>
              </div>

              <div
                data-testid="product:extended-warranty:detail"
                className={styles.policyDetail}
                dangerouslySetInnerHTML={{
                  __html: `${
                    warranty.selected
                      ? warranty.confirmation
                      : warranty.policyDetail
                  }`,
                }}
              />
            </section>
          );
        })}
      </div>
    </div>
  );
};
